// A walkthrough (lib/traceWalkthrough.js): an investigator walks the reader
// through the traces on a workspace and pins a note on each moment that tells
// their story. The workspace's chat runs one as a job of its own
// (walk_through, lib/chatSubject.js) and shows the story it tells in its
// transcript (WalkthroughCard): the moments numbered as their pins on the
// board are, where pointing at one lights its pin and a click opens its note
// under its card; which traces it read with nothing to pin and which it did
// not read; and, across several, how they connect. Beside a board, headless,
// BoardWalkthrough is what the chat and the board's bar start one through.
// A workspace's walkthrough reads every session on it together: where it read
// several, each moment says which trace it is on; where it read one, it reads
// as that trace's own. The refs it writes for the sessions it read (S1, S2,
// ...) are named for the reader (RefText): one session is "the session",
// several are tags naming their traces. What the chat the workspace is worked
// in claimed about its traces are leads it reads (lib/workspaceClaims.js), and
// so is what an investigation made of the case a workspace was opened for
// (lib/walkCases.js). Each moment says which of the claims and the case's
// notes it covers.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import { isWorkspaceWalk, publishBoardWalk, startWalkthrough, stopWalkthrough, useBoardWalk, useWalkthrough } from "../lib/traceWalkthrough.js";
import { boardCases } from "../lib/investigateWorkspace.js";
import { walkCases } from "../lib/walkCases.js";
import { readChatMessages, useChatClaims } from "../lib/workspaceClaims.js";
import { openWorkspaceNote, pointWorkspaceNote, requestWorkspaceFocus, useWorkspaceCanvas, useWorkspaceNotesPointed } from "../lib/workspaceStore.js";
import { saveTraceCanvas } from "../lib/traceCanvasStore.js";

const META = "text-[11px] leading-[1.3]";
// Traces a walkthrough did not read listed before the rest fold into a line.
const UNREAD_SHOWN = 3;
const EYEBROW = "text-[10px] uppercase tracking-[0.07em] leading-[1.2] text-[color:var(--eyebrow)]";
// A moment's colour, as its pin's: one not to miss as a medium finding's, one
// that carries the story in ink.
const TONE = { matters: "var(--severity-medium)", context: "var(--ink)" };

function SectionRow({ label, detail = null, action = null }) {
  return (
    <div className="flex min-w-0 items-baseline gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[4px]">
      <span className={`${EYEBROW} shrink-0 whitespace-nowrap`}>{label}</span>
      <span className={`${META} min-w-0 flex-1 truncate tabular-nums text-[color:var(--muted)]`}>{detail}</span>
      {action}
    </div>
  );
}

// A note's headline (its first sentence, as the walkthrough writes them) and
// the rest of what it says.
function split(text) {
  const m = /^(.{3,120}?[.!?])\s+([\s\S]+)$/.exec(String(text || "").trim());
  return m ? [m[1], m[2]] : [String(text || "").trim(), ""];
}

// The walkthrough's words split at the refs it wrote for the sessions it read
// (S1 up to S`count`): strings, and { ref, start } where a ref stands (`start`
// where it opens a sentence).
export function refParts(text, count) {
  const s = String(text || "");
  const out = [];
  if (!count) return [s];
  const re = /\bS([1-9]\d?)\b/g;
  let last = 0;
  for (let m = re.exec(s); m; m = re.exec(s)) {
    const ref = Number(m[1]);
    if (ref > count) continue;
    if (m.index > last) out.push(s.slice(last, m.index));
    out.push({ ref, start: !s.slice(0, m.index).trim() || /[.!?:]\s*$/.test(s.slice(0, m.index)) });
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

// The words with their refs named: with one session read, "S1" is the
// session; with several, each ref a tag naming its trace on hover, which a
// click brings into view (`onFocus(id)`, where the words are not inside a
// control of their own).
function RefText({ text, traces, nameOf, onFocus = null }) {
  const parts = refParts(text, traces.length);
  if (parts.length === 1 && typeof parts[0] === "string") return parts[0];
  if (traces.length === 1) return parts.map((p) => (typeof p === "string" ? p : p.start ? "The session" : "the session")).join("");
  return parts.map((p, i) => {
    if (typeof p === "string") return p;
    const id = traces[p.ref - 1]?.id;
    const tag = (
      <span className="mx-[1px] inline-block rounded-[2px] border border-[color:var(--line)] px-[3px] align-baseline text-[10.5px] leading-[14px] tabular-nums text-[color:var(--ink)]" data-walkthrough-ref={`S${p.ref}`}>
        {`S${p.ref}`}
      </span>
    );
    return (
      <HoverTooltip key={i} placement="top" label={nameOf(id)}>
        {onFocus ? <button type="button" onClick={() => onFocus(id)} className="cursor-pointer p-0">{tag}</button> : tag}
      </HoverTooltip>
    );
  });
}

// What a moment covers, in words ("Claim 3 and the case's note"): the chat's
// claims as they are numbered now (`claimN(id)`, else as they were when it
// was walked), and the case's notes; empty where it covers none.
export function coverWords(covers, claimN = () => null) {
  const claims = [...new Set((covers || []).filter((c) => c.kind === "claim").map((c) => claimN(c.id) ?? c.n).filter(Boolean))];
  const notes = (covers || []).filter((c) => c.kind === "case").length;
  const parts = [
    claims.length ? `${claims.length === 1 ? "Claim" : "Claims"} ${claims.join(", ")}` : "",
    notes ? (notes === 1 ? "the case's note" : `${notes} of the case's notes`) : "",
  ].filter(Boolean);
  return parts.join(" and ");
}

const whenOf = (at) => (at ? new Date(at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const money = (usd) => (usd > 0 ? `$${usd.toFixed(2)}` : "");

// `turn` none on a board that says where a moment is (`turned`) is on a page
// it has not read: "earlier"; none where it does not, nothing.
function Moment({ note, n, turn, turned = true, trace = null, place = null, covers = "", words, lit, onPoint, onOpen }) {
  const [headline, rest] = split(note.text);
  return (
    <button
      type="button"
      data-walkthrough-moment={note.id}
      data-lit={lit ? "" : undefined}
      onMouseEnter={() => onPoint?.(note.id)}
      onMouseLeave={() => onPoint?.(null)}
      onFocus={() => onPoint?.(note.id)}
      onBlur={() => onPoint?.(null)}
      onClick={() => onOpen?.(note.id)}
      className={`flex w-full min-w-0 cursor-pointer items-start gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[7px] text-left outline-none transition-colors focus-visible:bg-[color:var(--gray-fill)] ${lit ? "bg-[color:var(--gray-fill)]" : "hover:bg-[color:var(--gray-fill)]"}`}
    >
      <span
        aria-hidden="true"
        className="mt-[1px] grid h-[16px] min-w-[16px] shrink-0 place-items-center rounded-full px-[4px] text-[9.5px] font-semibold tabular-nums leading-none text-[color:var(--bg)]"
        style={{ background: TONE[note.kind] || TONE.context }}
      >
        {n}
      </span>
      {/* Across many traces, which one it is on is a line of its own under
          its words, so they keep the row's whole width. */}
      <span className="grid min-w-0 flex-1 gap-[2px]">
        <span className="text-[12px] leading-[1.45]">
          <span className="font-medium text-[color:var(--ink)]">{words(headline)}</span>
          {rest ? <span className="text-[color:var(--muted)]">{" "}{words(rest)}</span> : null}
        </span>
        {covers ? <span className={`${META} min-w-0 text-[color:var(--muted)]`} data-walkthrough-covers="">{`Covers ${covers}`}</span> : null}
        {trace ? (
          <span className={`${META} min-w-0 truncate tabular-nums text-[color:var(--muted-soft)]`} data-walkthrough-trace="">{[trace, place, turn].filter(Boolean).join(" \u00b7 ")}</span>
        ) : null}
      </span>
      {trace || !(turn || turned) ? null : (
        <span className={`${META} shrink-0 tabular-nums text-[color:var(--muted-soft)]`} data-walkthrough-turn="">{turn || "earlier"}</span>
      )}
    </button>
  );
}

// A trace the walkthrough has no moment on: one it read with nothing to pin
// there, or one on the workspace it did not read (laid on since, or past the
// traces it reads at most). A click brings it into view.
function LeftOut({ id, name, read, onFocus = null }) {
  const body = (
    <>
      <span className="min-w-0 flex-1 truncate text-[12px] leading-[1.45] text-[color:var(--ink)]">{name}</span>
      <span className={`${META} shrink-0 text-[color:var(--muted)]`}>{read ? "read, no moment on it" : "not read"}</span>
    </>
  );
  const row = "flex w-full min-w-0 items-baseline gap-[8px] border-b border-[color:var(--line-soft)] px-[9px] py-[6px] text-left";
  return onFocus ? (
    <button type="button" onClick={() => onFocus(id)} className={`${row} cursor-pointer outline-none transition-colors hover:bg-[color:var(--gray-fill)] focus-visible:bg-[color:var(--gray-fill)]`} data-walkthrough-left={read ? "read" : "unread"}>{body}</button>
  ) : (
    <div className={row} data-walkthrough-left={read ? "read" : "unread"}>{body}</div>
  );
}

// A connection between two traces the walkthrough found: from the one it
// leaves to the one it lands in, which a click brings into view.
function Link({ link, nameOf, words, onFocus }) {
  return (
    <button
      type="button"
      data-walkthrough-link={link.id}
      onClick={() => onFocus?.(link.to.session)}
      className="grid w-full min-w-0 cursor-pointer gap-[2px] border-b border-[color:var(--line-soft)] px-[9px] py-[7px] text-left outline-none transition-colors hover:bg-[color:var(--gray-fill)] focus-visible:bg-[color:var(--gray-fill)]"
    >
      <span className={`${META} min-w-0 truncate text-[color:var(--muted)]`}>{`${nameOf(link.from.session)} \u2192 ${nameOf(link.to.session)}`}</span>
      <span className="min-w-0 text-[12px] leading-[1.45] text-[color:var(--ink)]">{words(link.text)}</span>
    </button>
  );
}

// Beside a board, headless: what the workspace's chat (walk_through) and the
// board's bar start a walkthrough through, published by the walkthrough's key
// (publishBoardWalk): { key, walks, status, held, start(question), stop,
// board, sessions, turnLabel, claimN }, `walks` the key a start walks
// through (`next`, else `session`); the rest is what the story it tells in
// the chat says of the board (WalkthroughStory).
// `session`: the walkthrough's key (a trace's session, or its
// workspaceWalkKey); `title`, what the trace is called. `next`: what walking
// through walks, where that is not `session` (a trace's own walkthrough shown
// on a workspace that now holds more traces walks them all). A workspace's:
// `sessions`, the traces on it ([{ id, title, inside }], `inside` naming the
// workspace it holds that one is in). `turnLabel(messageId, session)`: the
// turn a moment is on, as the board prints it; none for one on a page of the
// record the board has not read. `workspaceChat`: the chat the workspace is
// worked in ({ chatId, graphKey }), whose claims (lib/walkClaims.js) the
// walkthrough reads as leads, with that chat to look into. `board`: the
// workspace's id, for the case it was opened for
// (lib/investigateWorkspace.js boardCases) and for the chat worked on it to
// find this one (boardWalkOf). A walkthrough starts only when asked.
export function BoardWalkthrough({ session, next = null, title = "", sessions: given = null, workspaceChat = null, board: boardId = null, turnLabel = null }) {
  const walk = useWalkthrough(session);
  const walkAs = next || session;
  const manyNext = isWorkspaceWalk(walkAs);
  // Each trace as its card on the board is called (what it did, read since
  // it was laid on), else as the workspace calls it.
  const board = useWorkspaceCanvas();
  const titlesKey = given ? JSON.stringify((board?.cards || []).map((c) => [String(c.id || "").toLowerCase(), c.title || ""])) : "";
  // Keyed by what they say, so traces handed anew at each draw publish
  // nothing new.
  const givenKey = given ? JSON.stringify(given.map((t) => [String(t.id || ""), t.title || "", t.inside || ""])) : "";
  const sessions = useMemo(() => {
    if (!givenKey) return null;
    const titled = new Map(titlesKey ? JSON.parse(titlesKey) : []);
    return JSON.parse(givenKey).map(([id, name, inside]) => ({ id, title: titled.get(id) || name, ...(inside ? { inside } : {}) }));
  }, [givenKey, titlesKey]);
  const claims = useChatClaims(workspaceChat);
  // The chat's claims as they are numbered now, by id, for what each moment
  // covers.
  const claimNs = useMemo(() => new Map(claims.map((c) => [String(c.id).toLowerCase(), c.n])), [claims]);
  const claimN = useCallback((id) => claimNs.get(String(id).toLowerCase()) ?? null, [claimNs]);
  const readChat = useMemo(() => (workspaceChat ? () => readChatMessages(workspaceChat) : null), [workspaceChat]);
  // Walking through: what the workspace holds now, with what was asked, and
  // what an investigation made of the case the workspace was opened for, as
  // it stands when the walkthrough starts. A workspace walked through is the
  // reader's.
  const begin = (question = "") => {
    if (boardId) saveTraceCanvas(String(boardId));
    return startWalkthrough(walkAs, { title, question, claims, cases: walkCases(boardCases(boardId)), readChat, ...(manyNext ? { sessions: (sessions || []).map((t) => ({ id: t.id, title: t.title || "" })) } : {}) });
  };
  const beginNow = useRef(begin);
  beginNow.current = begin;
  const walkAsNow = useRef(walkAs);
  walkAsNow.current = walkAs;
  const held = !!(walk.account || walk.notes.length);
  // Settles with the walkthrough as it ends.
  const start = useCallback((question = "") => beginNow.current(typeof question === "string" ? question : ""), []);
  const stop = useCallback(() => stopWalkthrough(walkAsNow.current), []);
  useEffect(() => {
    if (!session) return undefined;
    publishBoardWalk(session, { key: session, walks: walkAs, status: walk.status, held, start, stop, board: boardId ? String(boardId) : null, sessions, turnLabel, claimN });
    return undefined;
  }, [session, walkAs, walk.status, held, start, stop, boardId, sessions, turnLabel, claimN]);
  useEffect(() => () => publishBoardWalk(session, null), [session]);
  return null;
}

// The story a walkthrough tells (`walkKey`, its key), as the workspace's chat
// shows the one it ran: its moments numbered as their pins on the board,
// each lighting its pin while pointed at and opening its note under its card
// on a click (lib/workspaceStore.js, as the chat's own pins do), the traces
// it read with nothing to pin and the ones it did not read, and how they
// connect. Where the board it was walked on is in front, each moment says
// the turn it is on and which workspace there holds its trace
// (BoardWalkthrough). Nothing while it holds none.
export function WalkthroughStory({ walkKey }) {
  const walk = useWalkthrough(walkKey);
  const many = isWorkspaceWalk(walkKey);
  // Each trace as the board in front calls it (its card's name), else as the
  // workspace calls it, else as the walkthrough read it.
  const board = useWorkspaceCanvas();
  const beside = useBoardWalk(walkKey);
  const here = beside?.sessions || null;
  const turnLabel = beside?.turnLabel || null;
  const pointed = useWorkspaceNotesPointed();
  const lit = useMemo(() => new Set(pointed || []), [pointed]);
  const held = useRef(null);
  useEffect(() => () => { if (held.current) pointWorkspaceNote(null); }, []);
  // Every trace it did not read listed, past the first few.
  const [allUnread, setAllUnread] = useState(false);
  if (!walkKey || !(walk.account || walk.notes.length)) return null;
  const traces = walk.sessions || [];
  const several = many && traces.length > 1;
  const hereOf = (id) => (here || []).find((t) => t.id === String(id || "").toLowerCase()) || null;
  const nameOf = (id) => {
    const s = String(id || "").toLowerCase();
    const card = (board?.cards || []).find((c) => String(c.id || "").toLowerCase() === s);
    const at = traces.findIndex((t) => t.id === s);
    return card?.title || hereOf(s)?.title || (at < 0 ? "Another trace" : (traces[at].title || `Trace ${at + 1}`));
  };
  // Where a trace is, said after its name: inside a workspace the board
  // holds (its moments' pins on that workspace's card), or no longer on it
  // at all.
  const placeOf = (id) => {
    if (!here) return null;
    const t = hereOf(id);
    return !t ? "not on this workspace" : t.inside ? `in ${t.inside}` : null;
  };
  const point = (id) => {
    held.current = id;
    pointWorkspaceNote(id);
  };
  // A moment's or a connection's words, inside the control that opens it.
  const words = (text) => (many ? <RefText text={text} traces={traces} nameOf={nameOf} /> : text);
  // How much it covers: the traces it read (a trace's own walkthrough read
  // that trace), the ones it put a moment on, and the ones it was handed and
  // did not read.
  // Where the board it was walked on is in front, the traces it did not read
  // are the ones on that board now, laid on since included, and none taken
  // off since.
  const own = String(walkKey || "").toLowerCase();
  const readIds = (walk.read?.length ? walk.read : traces.length ? traces.map((t) => t.id) : [own]).filter(Boolean);
  const pinnedOn = new Set(walk.notes.map((n) => String(n.session || own).toLowerCase()));
  const manyTraces = readIds.length > 1 || traces.length > 1 || (here || []).length > 1;
  const readNone = manyTraces ? readIds.filter((id) => !pinnedOn.has(id)) : [];
  const unread = manyTraces
    ? [...new Set([...traces.map((t) => t.id), ...(here || []).map((t) => t.id)])].filter((id) => !readIds.includes(id) && (!here || here.some((t) => t.id === id)))
    : [];
  const unreadShown = allUnread || unread.length <= UNREAD_SHOWN + 1 ? unread : unread.slice(0, UNREAD_SHOWN);
  const leftOut = [...readNone.map((id) => ({ id, read: true })), ...unreadShown.map((id) => ({ id, read: false }))];
  const onCount = readIds.filter((id) => pinnedOn.has(id)).length;
  const momentWords = `${walk.notes.length} moment${walk.notes.length === 1 ? "" : "s"}${readIds.length < 2 || !walk.notes.length ? ""
    : onCount >= readIds.length ? ` across ${readIds.length === 2 ? "both" : `all ${readIds.length}`} traces` : ` on ${onCount} of the ${readIds.length} traces it read`}`;
  return (
    <div data-walkthrough="" className="grid min-w-0 rounded-[2px] border border-[color:var(--line-soft)]">
      <SectionRow
        label={walk.status === "running" ? "The last walkthrough" : "The story"}
        detail={[
          momentWords,
          walk.claims || walk.cases ? `with ${[walk.claims ? `${walk.claims} claim${walk.claims === 1 ? "" : "s"} from the chat` : "", walk.cases ? (walk.cases === 1 ? "the case's investigation" : `${walk.cases} cases' investigations`) : ""].filter(Boolean).join(" and ")}` : "",
          whenOf(walk.at),
          money(walk.costUsd),
        ].filter(Boolean).join(" \u00b7 ")}
      />
      {walk.account ? (
        <div className="min-w-0 border-b border-[color:var(--line-soft)] px-[9px] py-[7px] text-[12px] leading-[1.5] text-[color:var(--ink)]" data-walkthrough-account="">
          {many ? <RefText text={walk.account} traces={traces} nameOf={nameOf} onFocus={requestWorkspaceFocus} /> : walk.account}
        </div>
      ) : null}
      {walk.notes.map((note, i) => {
        // One trace read says its name only where its pins are not on the
        // board's own card of it.
        const place = many ? placeOf(note.session) : null;
        return (
          <Moment
            key={note.id}
            note={note}
            n={i + 1}
            turn={turnLabel ? turnLabel(note.messageId, note.session) : null}
            turned={!!turnLabel}
            trace={several || place ? nameOf(note.session) : null}
            place={place}
            covers={coverWords(note.covers, beside?.claimN || undefined)}
            words={words}
            lit={lit.has(note.id)}
            onPoint={point}
            onOpen={openWorkspaceNote}
          />
        );
      })}
      {leftOut.length ? (
        <>
          <SectionRow label="Not in the story" detail={[readNone.length ? `${readNone.length} read with no moment` : "", unread.length ? `${unread.length} not read` : ""].filter(Boolean).join(" \u00b7 ")} />
          {leftOut.map((t) => <LeftOut key={t.id} id={t.id} name={nameOf(t.id)} read={t.read} onFocus={requestWorkspaceFocus} />)}
          {unreadShown.length < unread.length ? (
            <button type="button" onClick={() => setAllUnread(true)} className={`${META} w-full cursor-pointer border-b border-[color:var(--line-soft)] px-[9px] py-[6px] text-left text-[color:var(--muted)] hover:text-[color:var(--ink)]`} data-walkthrough-unread-more="">
              {`${unread.length - unreadShown.length} more not read`}
            </button>
          ) : null}
        </>
      ) : null}
      {several ? (
        <>
          <SectionRow label="How they connect" detail={walk.links.length ? `${walk.links.length} connection${walk.links.length === 1 ? "" : "s"}` : "none found"} />
          {walk.links.map((link) => <Link key={link.id} link={link} nameOf={nameOf} words={words} onFocus={requestWorkspaceFocus} />)}
        </>
      ) : null}
    </div>
  );
}

// A walkthrough the chat ran (walk_through's details: { key, account,
// moments }), in its transcript: the story as the walkthrough holds it in
// this browser, else as the call returned it.
export default function WalkthroughCard({ details }) {
  const walk = useWalkthrough(details?.key || null);
  if (details?.key && (walk.account || walk.notes.length)) return <WalkthroughStory walkKey={details.key} />;
  const moments = Array.isArray(details?.moments) ? details.moments : [];
  if (!details?.account && !moments.length) return null;
  return (
    <div data-walkthrough="" data-walkthrough-kept="" className="grid min-w-0 gap-[4px] rounded-[2px] border border-[color:var(--line-soft)] px-[9px] py-[7px]">
      {details.account ? <div className="text-[12px] leading-[1.5] text-[color:var(--ink)]" data-walkthrough-account="">{details.account}</div> : null}
      {moments.map((m, i) => <div key={i} className={`${META} text-[color:var(--muted)]`}>{`${i + 1}. ${m}`}</div>)}
    </div>
  );
}
